import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { useAuthStore } from '../../../../stores/authStore.js'
import { api } from '../../../../lib/api.js'
import { setKindeSession, clearLocalTokens } from '../../lib/authSession.js'
import { getKindeOrgCode } from '../../lib/kindeOrg.js'
import { getHomeUrl } from '../../lib/homeUrl.js'
import {
  consumePostLoginRedirect,
  peekPostLoginRedirect,
  clearPostLoginRedirect,
} from '../../lib/postLoginRedirect.js'
import { redirectToExpectedTenant } from '../../lib/tenantRedirect.js'
import { KindeTokenAdapter } from '../../lib/KindeTokenAdapter.js'
import { ROLES, resolveEffectiveRole } from '../../../../lib/config/roles.js'

// Guards the automatic prompt=login retry to once per browser session so a
// user whose account is genuinely unprovisioned sees the error screen on the
// second pass instead of bouncing to Kinde forever.
export const CROSS_TENANT_RETRY_KEY = 'kindeCrossTenantRetried'

export const useKindeCallbackLogic = () => {
  const kindeAuth = useKindeAuth()
  const { getAccessToken, error, isLoading, login } = kindeAuth
  const navigate = useNavigate()
  const [errorMessage, setErrorMessage] = useState(null)
  const [checkInProgress, setChecksInProgress] = useState(true)
  const [countdown, setCountdown] = useState(30)

  useEffect(() => {
    const handleKindeAuth = async () => {
      try {
        if (error) {
          setErrorMessage(`Kinde auth error: ${JSON.stringify(error)}`)
          setChecksInProgress(false)
          return
        }

        if (isLoading) {
          return
        }

        const kindeToken = await getAccessToken()
        if (!kindeToken) {
          setErrorMessage('No token received from Kinde')
          setChecksInProgress(false)
          return
        }

        // Validate token before setting it
        const validationResult =
          KindeTokenAdapter.mapKindeTokenToLegacyFormat(kindeToken)
        if (validationResult.invalidReason) {
          // The SDK persisted this identity's refresh token during the code
          // exchange, before validation ran — drop every local artifact so
          // the rejected identity can't be silently restored on reload.
          clearLocalTokens()

          if (
            KindeTokenAdapter.isCrossTenantToken(kindeToken) &&
            !sessionStorage.getItem(CROSS_TENANT_RETRY_KEY)
          ) {
            // Kinde silently reused the other product's SSO identity.
            // prompt=login forces the credential form WITHOUT revoking the
            // shared session, so other tabs and the legacy app survive.
            sessionStorage.setItem(CROSS_TENANT_RETRY_KEY, '1')
            await login({ orgCode: getKindeOrgCode(), prompt: 'login' })
            return
          }

          setErrorMessage(validationResult.invalidReason)
          setChecksInProgress(false)
          return
        }

        sessionStorage.removeItem(CROSS_TENANT_RETRY_KEY)

        // Persist token for apiClient interceptor usage
        setKindeSession(kindeToken)

        // Restrict IMC users to their tenant's URL. The DB user record is not
        // fetched until bootstrap, so the token is the only role source here —
        // routed through the resolver so this site follows any precedence change.
        const effectiveRole = resolveEffectiveRole({
          tokenRole: validationResult.rol,
        })
        if (effectiveRole === ROLES.IMC) {
          try {
            const userData = await api.getUserById(validationResult.uid)
            if (
              // Carry the stashed destination across the host hop as a URL
              // param — sessionStorage doesn't cross origins. Peek (not
              // consume) so a same-tenant user keeps the stash for the
              // SPA navigation below.
              redirectToExpectedTenant(userData?.tenant, {
                redirectTo: peekPostLoginRedirect(),
              })
            ) {
              clearPostLoginRedirect()
              return
            }
          } catch (userError) {
            console.error('Failed to verify user tenant:', userError)
            setErrorMessage('Unable to verify your access. Please try again.')
            setChecksInProgress(false)
            return
          }
        }

        // SPA navigation, not a reload: the SDK session is live in memory
        // after the code exchange, and a hard redirect here is what aborted
        // in-flight bootstrap fetches and looped Safari logins (MV-3293).
        // ProtectedLayout mounts on arrival and runs the bootstrap once.
        navigate(consumePostLoginRedirect() ?? getHomeUrl(), { replace: true })
      } catch (err) {
        setErrorMessage(
          `Failed to handle Kinde authentication: ${JSON.stringify(err)}`,
        )
        setChecksInProgress(false)
        return
      }
    }
    handleKindeAuth()
  }, [getAccessToken, error, isLoading, login, navigate])

  const handleReturnToLogin = useCallback(() => {
    // Local teardown only — this path also fires unattended via the 30s
    // countdown, and automatic paths must never revoke the shared Kinde SSO
    // session (it would log out every other tab and the legacy app).
    useAuthStore.getState().clearUser()
    clearLocalTokens()

    // Hard reload so the SDK cold-boots without the cleared refresh token;
    // an SPA navigate would keep its in-memory session alive and bounce the
    // login page straight back into the app.
    window.location.href = '/login'
  }, [])

  const handleSignInDifferentAccount = useCallback(async () => {
    useAuthStore.getState().clearUser()
    clearLocalTokens()
    try {
      // prompt=login forces fresh credentials without revoking the shared
      // SSO session — the sanctioned escape from a wrong-identity session.
      await login({ orgCode: getKindeOrgCode(), prompt: 'login' })
    } catch (loginError) {
      console.error('Kinde login failed:', loginError)
    }
  }, [login])

  // Countdown timer effect for error state
  useEffect(() => {
    if (errorMessage && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (errorMessage && countdown <= 0) {
      // Auto-redirect when countdown reaches zero
      handleReturnToLogin()
    }
  }, [errorMessage, countdown, handleReturnToLogin])

  return {
    isLoading,
    checkInProgress,
    errorMessage,
    countdown,
    handleReturnToLogin,
    handleSignInDifferentAccount,
  }
}
